import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { AssetCounts, FolderFacet, FolderTreeEntry, TagFacet } from '@xphoto/shared';
import { ApiError, fetchCounts, fetchFolderTree, fetchFolders, fetchTags } from './api';

/**
 * Chargement des facettes (dossiers, tags) et des compteurs de la barre d'outils.
 * Hooks maison symétriques à `useAssets` : `fetch` + parse Zod, annulation à la
 * navigation, états loading/erreur/vide propres. Rechargés à chaque `libraryId`
 * et sur `refreshKey` (p. ex. après un scan).
 */

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  errorCode: string | null;
}

function useAsync<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    loading: true,
    errorCode: null,
  });

  const run = useCallback(fn, deps);

  useEffect(() => {
    const controller = new AbortController();
    // CONSERVE les données précédentes pendant le rechargement (audit F10) — même
    // discipline que `CountsSource` plus bas. Remettre `data` à `null` provoquait un
    // FLASH DE SQUELETTE à chaque descente dans un dossier (les facettes se rechargent
    // sur `parent`), alors que la liste précédente reste un affichage acceptable le
    // temps d'un aller-retour. Le drapeau `loading` porte déjà l'information.
    setState((prev) => ({ data: prev.data, loading: true, errorCode: null }));
    run(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setState({ data, loading: false, errorCode: null });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          data: null,
          loading: false,
          errorCode: err instanceof ApiError ? err.code : 'NETWORK',
        });
      });
    return () => controller.abort();
  }, [run]);

  return state;
}

/**
 * Facettes de dossiers. `parent` (optionnel, défaut `''` = racine) scope la navigation
 * hiérarchique aux SOUS-DOSSIERS DIRECTS de `parent` (façon explorateur). Ajouté en
 * DERNIER argument pour ne pas casser les appelants existants (EXIF) qui n'en ont pas
 * besoin (ils listent toujours la racine).
 *
 * `enabled = false` ⇒ INERTE (aucune requête, données vides). Voir la note sur
 * `useTags` : sur l'onglet Galerie — le cas nominal — la réponse était jetée.
 */
export function useFolders(
  libraryId: string,
  refreshKey = 0,
  parent = '',
  enabled = true,
): AsyncState<FolderFacet[]> {
  const state = useAsync(
    async (signal) =>
      libraryId && enabled ? (await fetchFolders(libraryId, parent, signal)).folders : [],
    [libraryId, refreshKey, parent, enabled],
  );
  return state;
}

/**
 * ARBRE DES DOSSIERS de l'onglet Dossiers (XPHOTO-32, `ADR-0013` amendé).
 *
 * Même forme et mêmes garanties que `useFolders` (scopé au `parent` courant, inerte
 * quand `enabled = false`, données précédentes conservées pendant le rechargement),
 * mais alimenté par `/folder-tree` : les répertoires VIDES en font partie, alors
 * qu'ils sont invisibles pour les facettes SQL (elles sont dérivées du chemin des
 * photos). `useFolders` reste la source des écrans qui n'ont besoin que des facettes
 * (EXIF) et n'a donc PAS été supprimée.
 */
export function useFolderTree(
  libraryId: string,
  refreshKey = 0,
  parent = '',
  enabled = true,
): AsyncState<FolderTreeEntry[]> {
  return useAsync(
    async (signal) =>
      libraryId && enabled ? (await fetchFolderTree(libraryId, parent, signal)).folders : [],
    [libraryId, refreshKey, parent, enabled],
  );
}

/**
 * Facettes de tags. `enabled = false` ⇒ INERTE (aucune requête, données vides).
 *
 * ⚠ PERF (audit A3) : `GalleryView` montait `useFolders` ET `useTags` sans condition,
 * alors que `folders` n'est consommé qu'en mode explorateur de dossiers et `tags` que sur
 * l'onglet « Tags ». Sur l'onglet Galerie — LE cas nominal — les deux réponses étaient
 * intégralement téléchargées puis JETÉES. Le drapeau rend la condition explicite côté
 * appelant, au lieu de la déguiser en `libraryId` vide.
 */
export function useTags(libraryId: string, refreshKey = 0, enabled = true): AsyncState<TagFacet[]> {
  const state = useAsync(
    async (signal) => (libraryId && enabled ? (await fetchTags(libraryId, signal)).tags : []),
    [libraryId, refreshKey, enabled],
  );
  return state;
}

// --- Compteurs partagés (barre d'outils + badge « À traiter ») -----------------
//
// ⚠ SOURCE PARTAGÉE (perf) : les compteurs d'une bibliothèque vivent dans UN store
// module-level partagé par tous les appelants de `useCounts` via `useSyncExternalStore`.
// Avant, la coquille (`AppChrome`, badge « À traiter ») ET la galerie (`GalleryView`,
// badge « Avec son »), montées SIMULTANÉMENT, faisaient chacune leur `GET /counts` →
// DEUX requêtes identiques par ouverture de galerie. Désormais : UNE requête partagée.
// Bonus correctness : le badge d'`AppChrome` (jusque-là figé, sans `refreshKey`) se met
// à jour quand `GalleryView` bumpe son `refreshKey` (après un déplacement/suppression).

const EMPTY_COUNTS: AssetCounts = {
  present: 0,
  byKind: { image: 0, animatedImage: 0, video: 0 },
  withAudio: 0,
  aiReview: 0,
};

class CountsSource {
  private state: AsyncState<AssetCounts>;
  private readonly listeners = new Set<() => void>();
  private refCount = 0;
  private controller: AbortController | null = null;
  private loaded = false;

  constructor(private readonly libraryId: string) {
    this.state = libraryId
      ? { data: null, loading: true, errorCode: null }
      : { data: EMPTY_COUNTS, loading: false, errorCode: null };
  }

  getSnapshot = (): AsyncState<AssetCounts> => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    this.refCount += 1;
    if (this.refCount === 1 && this.libraryId && !this.loaded) {
      this.loaded = true;
      this.fetch();
    }
    return () => {
      this.listeners.delete(listener);
      this.refCount -= 1;
      if (this.refCount === 0) this.reset();
    };
  };

  /** Force un rechargement (appelé quand un `refreshKey` d'appelant change). */
  refresh(): void {
    if (this.libraryId) this.fetch();
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private fetch(): void {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    // Conserve les données précédentes pendant un rechargement (pas de flash).
    this.state = { data: this.state.data, loading: true, errorCode: null };
    this.emit();
    fetchCounts(this.libraryId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        this.state = { data, loading: false, errorCode: null };
        this.emit();
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        this.state = {
          data: null,
          loading: false,
          errorCode: err instanceof ApiError ? err.code : 'NETWORK',
        };
        this.emit();
      });
  }

  private reset(): void {
    this.controller?.abort();
    this.controller = null;
    this.loaded = false;
    this.state = this.libraryId
      ? { data: null, loading: true, errorCode: null }
      : { data: EMPTY_COUNTS, loading: false, errorCode: null };
  }
}

const countsSources = new Map<string, CountsSource>();

function getCountsSource(libraryId: string): CountsSource {
  let source = countsSources.get(libraryId);
  if (!source) {
    source = new CountsSource(libraryId);
    countsSources.set(libraryId, source);
  }
  return source;
}

export function useCounts(libraryId: string, refreshKey = 0): AsyncState<AssetCounts> {
  const source = getCountsSource(libraryId);
  const state = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
  // Un changement de `refreshKey` (après un scan/déplacement/suppression) force UN
  // rechargement partagé. Le premier montage est couvert par `subscribe` (pas de
  // double chargement) : on ne déclenche que sur un VRAI changement de clé.
  const prevKey = useRef(refreshKey);
  useEffect(() => {
    if (prevKey.current !== refreshKey) {
      prevKey.current = refreshKey;
      source.refresh();
    }
  }, [source, refreshKey]);
  return state;
}
